import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ScheduledJobsService, type ScheduledJob } from '@/services/ScheduledJobsService';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/Card/Card';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { Switch } from '@/components/ui/Switch/Switch';
import { Alert } from '@/components/ui/Alert/Alert';
import { useNotificationStore } from '@/stores/notificationStore';
import { AlertTriangle, Clock, CheckCircle } from 'lucide-react';
import cronstrue from 'cronstrue';

export function ScheduledJobs() {
  const queryClient = useQueryClient();
  const { success, error } = useNotificationStore();
  const service = new ScheduledJobsService();

  // Local state for editing
  const [editedJobs, setEditedJobs] = useState<Record<string, Partial<ScheduledJob>>>({});

  // Fetch scheduled jobs
  const { data: jobs, isLoading } = useQuery({
    queryKey: ['scheduled-jobs'],
    queryFn: () => service.list(),
    staleTime: 30000, // 30 seconds
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, config }: { id: string; config: Partial<ScheduledJob> }) =>
      service.update(id, {
        enabled: config.enabled,
        schedule_cron: config.schedule_cron,
        metadata: config.metadata
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] });
      setEditedJobs({});
      success('Scheduled job updated successfully');
    },
    onError: (err: any) => {
      error(err?.message || 'Failed to update scheduled job');
    }
  });

  const handleToggle = (jobId: string, enabled: boolean) => {
    setEditedJobs(prev => ({
      ...prev,
      [jobId]: { ...prev[jobId], enabled }
    }));
  };

  const handleScheduleChange = (jobId: string, schedule_cron: string) => {
    setEditedJobs(prev => ({
      ...prev,
      [jobId]: { ...prev[jobId], schedule_cron }
    }));
  };

  const handleMetadataChange = (jobId: string, key: string, value: string) => {
    setEditedJobs(prev => ({
      ...prev,
      [jobId]: {
        ...prev[jobId],
        metadata: {
          ...((prev[jobId]?.metadata || jobs?.find(j => j.id === jobId)?.metadata) as any),
          [key]: parseInt(value, 10) || 0
        }
      }
    }));
  };

  const handleSave = (jobId: string) => {
    const job = jobs?.find(j => j.id === jobId);
    const edited = editedJobs[jobId];
    if (!job || !edited) return;

    updateMutation.mutate({
      id: jobId,
      config: edited
    });
  };

  const formatJobType = (type: string): string => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatCronDescription = (cronExpression: string): string => {
    try {
      return cronstrue.toString(cronExpression);
    } catch {
      return 'Invalid cron expression';
    }
  };

  const getJobValue = (job: ScheduledJob, field: keyof ScheduledJob) => {
    return editedJobs[job.id]?.[field] ?? job[field];
  };

  const getMetadataValue = (job: ScheduledJob, key: string) => {
    return editedJobs[job.id]?.metadata?.[key] ?? job.metadata[key] ?? '';
  };

  const isJobEdited = (jobId: string) => {
    return !!editedJobs[jobId] && Object.keys(editedJobs[jobId]).length > 0;
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader 
          title="Scheduled Jobs" 
          description="Configure automated background jobs"
        />
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader 
        title="Scheduled Jobs" 
        description="Configure automated background jobs that run on a schedule"
      />

      <div className="space-y-6">
        {jobs?.map(job => {
          const enabled = getJobValue(job, 'enabled') as boolean;
          const scheduleCron = getJobValue(job, 'schedule_cron') as string;
          const isEdited = isJobEdited(job.id);

          return (
            <Card key={job.id}>
              <CardHeader>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <CardTitle as="h3" className="text-lg">{formatJobType(job.job_type)}</CardTitle>
                    {enabled && <span className="px-2 py-0.5 bg-success-100 text-success-700 text-xs font-medium rounded">Active</span>}
                  </div>
                  <CardDescription className="mt-1">
                    {enabled ? (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatCronDescription(scheduleCron)}
                      </span>
                    ) : (
                      'Disabled'
                    )}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Switch 
                    checked={enabled}
                    onChange={(e) => handleToggle(job.id, e.target.checked)}
                  />
                </div>
              </CardHeader>

              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Schedule (Cron Expression)
                    </label>
                    <Input 
                      value={scheduleCron}
                      onChange={(e) => handleScheduleChange(job.id, e.target.value)}
                      placeholder="*/10 * * * *"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatCronDescription(scheduleCron)}
                    </p>
                  </div>

                  {job.job_type === 'venue_enrichment_scheduler' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Max Items to Fetch
                        </label>
                        <Input 
                          type="number"
                          min="1"
                          max="1000"
                          value={getMetadataValue(job, 'max_items_to_fetch')}
                          onChange={(e) => handleMetadataChange(job.id, 'max_items_to_fetch', e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Maximum venues to schedule per run (1-1000)
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Staleness Threshold (days)
                        </label>
                        <Input 
                          type="number"
                          min="1"
                          max="365"
                          value={getMetadataValue(job, 'staleness_days')}
                          onChange={(e) => handleMetadataChange(job.id, 'staleness_days', e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Days before enrichment is considered stale (1-365)
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {job.last_run_at && (
                  <Alert 
                    variant={job.last_run_status === 'failed' ? 'error' : 'info'} 
                    className="mt-4"
                  >
                    <div className="flex items-start gap-2">
                      {job.last_run_status === 'failed' ? (
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">
                          Last Run: {new Date(job.last_run_at).toLocaleString()}
                        </p>
                        {job.last_run_status === 'failed' && job.last_run_error && (
                          <p className="text-xs mt-1 text-error-700">{job.last_run_error}</p>
                        )}
                      </div>
                    </div>
                  </Alert>
                )}
              </CardContent>

              <CardFooter>
                <Button 
                  onClick={() => handleSave(job.id)} 
                  disabled={updateMutation.isPending || !isEdited}
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>
    </PageContainer>
  );
}

